import { Component, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { PatronAuthService, PatronUser } from '../../core/services/patron-auth.service';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule, RouterLink],
  template: `
    <div class="auth-page">
      <div class="auth-container">
        <div class="auth-card">
          <div class="auth-card__header">
            <div class="auth-card__icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#1b8354" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#1b8354" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h1 class="auth-card__title">{{ "ChangePassword.title" | translate }}</h1>
            <p class="auth-card__subtitle">{{ "ChangePassword.subtitle" | translate }}</p>
          </div>

          @if (!currentUser) {
            <!-- Not logged in — show guard message -->
            <div class="auth-guard">
              <p>{{ "ChangePassword.notLoggedIn" | translate }}</p>
              <a routerLink="/home" class="auth-btn auth-btn--primary" style="text-decoration:none;display:inline-block;margin-top:12px;">
                {{ "ChangePassword.loginLink" | translate }}
              </a>
            </div>
          } @else if (success()) {
            <!-- Success state -->
            <div class="auth-success">
              <div class="auth-success__icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="#1b8354" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M22 4L12 14.01L9 11.01" stroke="#1b8354" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h2>{{ "ChangePassword.successMessage" | translate }}</h2>
              <a routerLink="/home" class="auth-btn auth-btn--primary" style="text-decoration:none;display:inline-block;margin-top:16px;">{{ "Nav.Home" | translate }}</a>
            </div>
          } @else {
            <!-- Change password form -->
            <form class="auth-form" (ngSubmit)="onSubmit()" novalidate>
              <div class="auth-form__group">
                <label for="oldPassword" class="auth-form__label">{{ "ChangePassword.oldPassword" | translate }} <span class="required">*</span></label>
                <div class="auth-form__password-wrapper">
                  <input id="oldPassword" [type]="showOld() ? 'text' : 'password'" class="auth-form__input"
                    [(ngModel)]="oldPassword" name="oldPassword" required
                    [placeholder]="'ChangePassword.oldPasswordPlaceholder' | translate"
                    [class.is-invalid]="submitted() && !oldPassword">
                  <button type="button" class="auth-form__eye-btn" (click)="showOld.set(!showOld())">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path [attr.d]="showOld() ? 'M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8' : 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8'" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                </div>
              </div>

              <div class="auth-form__group">
                <label for="newPassword" class="auth-form__label">{{ "ChangePassword.newPassword" | translate }} <span class="required">*</span></label>
                <div class="auth-form__password-wrapper">
                  <input id="newPassword" [type]="showNew() ? 'text' : 'password'" class="auth-form__input"
                    [(ngModel)]="newPassword" name="newPassword" required minlength="8"
                    [placeholder]="'ChangePassword.newPasswordPlaceholder' | translate"
                    [class.is-invalid]="submitted() && !newPassword">
                  <button type="button" class="auth-form__eye-btn" (click)="showNew.set(!showNew())">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path [attr.d]="showNew() ? 'M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8' : 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8'" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                </div>
              </div>

              <div class="auth-form__group">
                <label for="confirmPassword" class="auth-form__label">{{ "ChangePassword.confirmPassword" | translate }} <span class="required">*</span></label>
                <input id="confirmPassword" type="password" class="auth-form__input"
                  [(ngModel)]="confirmPassword" name="confirmPassword" required
                  [placeholder]="'ChangePassword.confirmPasswordPlaceholder' | translate"
                  [class.is-invalid]="submitted() && newPassword !== confirmPassword">
              </div>

              @if (errorKey()) {
                <div class="auth-error">{{ errorKey() | translate }}</div>
              }

              <button type="submit" class="auth-btn auth-btn--primary" [class.loading]="isLoading()">
                @if (isLoading()) {
                  <span class="auth-spinner"></span>
                }
                {{ "ChangePassword.submit" | translate }}
              </button>
            </form>
          }
        </div>
      </div>
    </div>
  `,
  styles: [`
    .auth-page {
      min-height: 100vh;
      background: linear-gradient(135deg, #f0faf5 0%, #e8f5ee 50%, #f5f7fa 100%);
      display: flex; align-items: center; justify-content: center; padding: 40px 16px;
    }
    .auth-container { width: 100%; max-width: 480px; }
    .auth-card { background: #fff; border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.08); padding: 40px; }
    @media (max-width: 480px) { .auth-card { padding: 24px; } }
    .auth-card__header { text-align: center; margin-bottom: 32px; }
    .auth-card__icon { width: 72px; height: 72px; background: #f0faf5; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
    .auth-card__title { font-size: 24px; font-weight: 700; color: #111; margin: 0 0 8px; }
    .auth-card__subtitle { font-size: 14px; color: #666; margin: 0; }
    .auth-form__group { margin-bottom: 16px; }
    .auth-form__label { display: block; font-size: 13px; font-weight: 600; color: #333; margin-bottom: 6px; }
    .required { color: #e53e3e; }
    .auth-form__input {
      width: 100%; padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 8px;
      font-size: 14px; color: #111; outline: none; transition: border-color 0.2s; box-sizing: border-box;
      &:focus { border-color: #1b8354; box-shadow: 0 0 0 3px rgba(27,131,84,0.08); }
      &.is-invalid { border-color: #e53e3e; }
    }
    .auth-form__password-wrapper { position: relative; }
    .auth-form__password-wrapper .auth-form__input { padding-inline-end: 42px; }
    .auth-form__eye-btn { position: absolute; inset-inline-end: 12px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: #666; padding: 0; }
    .auth-btn { width: 100%; padding: 12px; border-radius: 8px; font-size: 15px; font-weight: 600; cursor: pointer; border: none; transition: all 0.2s; display: flex; align-items: center; justify-content: center; gap: 8px;
      &--primary { background: #1b8354; color: #fff; &:hover { background: #16a362; } &.loading { opacity: 0.7; pointer-events: none; } }
    }
    .auth-spinner { width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff; border-radius: 50%; animation: spin 0.7s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
    .auth-error { background: #fff5f5; border: 1px solid #fed7d7; color: #c53030; border-radius: 8px; padding: 10px 14px; font-size: 13px; margin-bottom: 16px; }
    .auth-guard { text-align: center; padding: 16px 0; p { color: #555; margin-bottom: 8px; } }
    .auth-success { text-align: center; padding: 20px 0;
      &__icon { margin-bottom: 16px; }
      h2 { font-size: 18px; font-weight: 600; color: #1b8354; margin-bottom: 8px; }
    }
  `]
})
export class ForgotPasswordComponent implements OnInit {
  private patronAuth = inject(PatronAuthService);

  currentUser: PatronUser | null = null;

  oldPassword = '';
  newPassword = '';
  confirmPassword = '';

  errorKey = signal('');
  isLoading = signal(false);
  success = signal(false);
  submitted = signal(false);
  showOld = signal(false);
  showNew = signal(false);

  ngOnInit(): void {
    this.patronAuth.currentUser$.subscribe(user => {
      this.currentUser = user;
    });
  }

  onSubmit(): void {
    this.submitted.set(true);
    this.errorKey.set('');

    if (!this.oldPassword || !this.newPassword || !this.confirmPassword) {
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.errorKey.set('ChangePassword.errorPasswordMatch');
      return;
    }

    this.isLoading.set(true);

    this.patronAuth.changePassword(
      this.currentUser!.patron_id,
      this.oldPassword,
      this.newPassword,
      this.confirmPassword
    ).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.success.set(true);
      },
      error: () => {
        this.isLoading.set(false);
        this.errorKey.set('ChangePassword.errorGeneric');
      }
    });
  }
}

